import React, {ReactNode, useCallback, useMemo, useState} from "react"
import DataTable from "./DataTable";
import {useNavigate, useSearchParams} from "react-router-dom";
import {faChevronLeft, faChevronRight} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Autocomplete} from "../form/auto-complete/Autocomplete";
import {Input} from "../form/Input";
import {faArrowDown, faArrowUp} from "@fortawesome/pro-regular-svg-icons";
import {DynamicDataTableFilter, TableProps} from "./types";
import {transformToFancyAutocompleteOptions, transformToStringMapObject} from "./transformOptions";
import {FancyAutocomplete} from "../form/auto-complete/FancyAutocomplete";
import {enrichRowWithSearchableString, paginateRows, searchRows, sortRows} from "./searchSortFilter";


export interface DynamicDataTableProps<T> extends TableProps<T> {
  searchableProperties: Array<keyof T>
  availableFilters: DynamicDataTableFilter<T>[]
}

const PAGINATE_SIZE = 10
export default function DynamicDataTable<T>(props: DynamicDataTableProps<T>): ReactNode {
  const [searchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [filterValues, setFilterValues] = useState<Record<keyof T, string>>({} as Record<keyof T, string>)
  const [page, setPage] = useState(1)

  const [sortColumn, setSortColumn] = useState<keyof T | null>(null)
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC" | null>(null)

  const navigate = useNavigate()
  const setFilterValue = useCallback((filter: keyof T, value: string) => {
    setPage(1)
    setFilterValues(old => {
      const filters = {
        ...old,
        [filter]: value
      }
      navigate('?' + Object.entries(filters).map(([key, value]) => `${key}=${value}`).join('&'))
      return filters
    })
  }, [setFilterValues])

  const getDefaultFilterValue = useCallback((filterProperty: keyof T) => {
    const filter = props.availableFilters.find(f => f.property === filterProperty)
    return searchParams.get(filterProperty.toString()) ?? filter?.defaultValue
  }, [searchParams, props.availableFilters])

  const getFilterValue = useCallback((filter: keyof T) => filterValues[filter] ?? getDefaultFilterValue(filter), [getDefaultFilterValue, filterValues])

  const filterableData = useMemo(() => {
    return enrichRowWithSearchableString(props.data, props.searchableProperties)
  }, [props.data, props.searchableProperties])

  const filteredData = useMemo(() => {
    return filterableData.filter(row => {
      for (const filter of props.availableFilters) {
        const callback = filter.filterCallback ?? ((row: T, value: string) => row[filter.property] === value)
        const result = callback(row, getFilterValue(filter.property))
        if (!result) {
          return false
        }
      }
      // All filters have passed
      return true
    })
  }, [filterableData, props.availableFilters, getFilterValue])

  const searchedData = useMemo(() => {
    return searchRows(filteredData, search)
  }, [filteredData, search])

  const paginatedData = useMemo(() => {
    let sortedData = sortRows(searchedData, sortOrder, sortColumn)
    return paginateRows(sortedData, page, PAGINATE_SIZE)
  }, [searchedData, sortOrder, sortColumn, page])

  const maxPages = useMemo(() => Math.floor(Math.max(searchedData.length - 1, 0) / PAGINATE_SIZE) + 1, [searchedData])

  const changeSortSettings = useCallback(function <T>(updatedColumn: keyof T) {
    // @ts-ignore
    if (sortColumn !== updatedColumn) {
      // @ts-ignore
      setSortColumn(updatedColumn)
      setSortOrder("ASC")
      return
    }
    // Cycle the sort order
    if (sortOrder === "ASC") {
      setSortOrder("DESC")
    } else if (sortOrder === "DESC") {
      setSortOrder(null)
    } else {
      setSortOrder("ASC")
    }
  }, [sortColumn, sortOrder])

  const sortingHeaderMapper = useCallback(function <T>(props: TableProps<T>): ReactNode[] {
    const headers = props.columns.map((column) =>
      <th
        key={column.header}
        className={`${props.compact ? 'px-3 py-3' : 'px-6 py-3'} bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap cursor-pointer hover:bg-gray-100`}
        onClick={() => changeSortSettings<T>(column.property)}
      >
        <div className={"flex items-center justify-between"}>
          <span>{column.header}</span>
          {/* @ts-ignore */}
          {sortColumn === column.property && sortOrder !== null &&
            <FontAwesomeIcon icon={sortOrder === "ASC" ? faArrowUp : faArrowDown}/>}
        </div>
      </th>
    )
    if (props.onClickRow) {
      headers.push(<th key={'_actions'}
                       className={(props.compact ? 'px-3 py-3' : 'px-6 py-3') + " bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"}></th>)
    }
    return headers
  }, [changeSortSettings, sortColumn, sortOrder, props.columns, props.onClickRow])

  return <div>
    <div
      className={'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-x-4 gap-y-3 mb-4'}>
      <div className={"max-w"}>
        <Input type={'text'} label={'Zoeken'} value={search} onChange={(v: string) => {
          setSearch(v);
          setPage(1)
        }}/>
      </div>
      {props.availableFilters.map((filter, i) => {
          if (filter.type === 'autocomplete') {
            return <div key={i} className={`${getColSpanClass(filter.colSpan)}`}>
              <Autocomplete label={filter.title} options={transformToStringMapObject(filter.options)} width={"full"}
                            value={getFilterValue(filter.property)} onChange={(v) => setFilterValue(filter.property, v)}/>
            </div>
          } else if (filter.type === 'fancy-autocomplete') {
            return <div key={i} className={`${getColSpanClass(filter.colSpan)}`}>
              <FancyAutocomplete label={filter.title} options={transformToFancyAutocompleteOptions(filter.options)}
                                 value={getFilterValue(filter.property)} width={'full'}
                                 onChange={(v) => setFilterValue(filter.property, v)}/>
            </div>
          } else {
            return <div key={i} className={`${getColSpanClass(filter.colSpan)}`}>
              <Autocomplete label={filter.title} options={transformToStringMapObject(filter.options)} width={'full'}
                            value={getFilterValue(filter.property)} onChange={(v) => setFilterValue(filter.property, v)}/>
            </div>
          }
        }
      )}
    </div>
    <DataTable {...props} data={paginatedData} headerMapper={sortingHeaderMapper}/>
    <div className={'flex items-center justify-center space-x-4 mt-4 text-brand-on-surface'}>
      <button className={'h-8 w-8 rounded-full'} onClick={() => setPage(p => Math.max(p - 1, 1))}><FontAwesomeIcon
        icon={faChevronLeft} className={`${page <= 1 && 'text-slate-300'}`}/></button>
      <strong>
        {searchedData.length} resultaten
      </strong>
      <div>
        pagina
        <input type="number" className={'border text-center mx-2'} min={1} max={maxPages} value={page}
               onChange={(e) => setPage(Number(e.target.value))}/>
        / {maxPages}
      </div>
      <button className={'h-8 w-8 rounded-full'} onClick={() => setPage(p => Math.min(p + 1, maxPages))}>
        <FontAwesomeIcon icon={faChevronRight} className={`${page >= maxPages && 'text-brand-primary'}`}/></button>
    </div>
  </div>
}

function getColSpanClass(colSpan?: DynamicDataTableFilter<unknown>['colSpan']) {
  switch (colSpan) {
    case 2:
      return 'col-span-2'
    case 3:
      return 'col-span-3'
    case 4:
      return 'col-span-4'
    case 5:
      return 'col-span-5'
    case 6:
      return 'col-span-6'
    case 7:
      return 'col-span-7'
    case 8:
      return 'col-span-8'
    case 9:
      return 'col-span-9'
    case 10:
      return 'col-span-10'
    case 11:
      return 'col-span-11'
    case 12:
      return 'col-span-12'
    default:
      return 'col-span-1'
  }
}
